import React, { useMemo } from 'react';
import {
  getLeadershipChartBarColor,
  getLeadershipChartLegendColor,
} from '../../Utils/partnerLeadershipStatusChartColors';

export { LEADERSHIP_STATUS_COLORS } from '../../Utils/partnerLeadershipStatusChartColors';

const LeadershipCompanyStackedChart = ({
  companies = [],
  legend = [],
  emptyMessage = 'No company data available.',
  onCompanyTotalClick,
  onCompanyStatusClick,
}) => {
  const maxCompanyTotal = useMemo(
    () => Math.max(1, ...companies.map((company) => company.total || 0)),
    [companies],
  );

  const activeLegend = useMemo(() => {
    if (legend.length) {
      return legend;
    }
    return Array.from(new Set(
      companies.flatMap((company) => (company.statuses || []).map((item) => item.status)),
    ));
  }, [companies, legend]);

  if (!companies.length) {
    return <p className="leadership-empty">{emptyMessage}</p>;
  }

  return (
    <div className="leadership-company-stack">
      <div className="leadership-company-stack-legend" aria-label="Project status legend">
        {activeLegend.map((status) => (
          <span key={status} className="leadership-company-stack-legend-item">
            <span
              className="leadership-company-stack-legend-swatch"
              style={{ backgroundColor: getLeadershipChartLegendColor(status) }}
            />
            {status}
          </span>
        ))}
      </div>

      <div className="leadership-company-stack-list leadership-company-stack-list--scroll">
        {companies.map((company) => {
          const barWidthPercent = ((company.total || 0) / maxCompanyTotal) * 100;
          const rowLabel = company.displayName || company.companyName;

          return (
            <div key={company.companyName} className="leadership-company-stack-row">
              <span className="leadership-company-stack-label" title={rowLabel}>
                {rowLabel}
              </span>
              <div className="leadership-company-stack-bar-area">
                <div
                  className="leadership-company-stack-track"
                  style={{ width: `${barWidthPercent}%` }}
                  aria-hidden="true"
                >
                  {(company.statuses || []).map((statusItem) => {
                    const widthPercent = company.total > 0
                      ? (statusItem.count / company.total) * 100
                      : 0;

                    const segmentProps = onCompanyStatusClick
                      ? {
                        role: 'button',
                        tabIndex: 0,
                        className: 'leadership-company-stack-segment leadership-company-stack-segment--clickable',
                        onClick: () => onCompanyStatusClick({
                          companyName: company.companyName,
                          status: statusItem.status,
                          count: statusItem.count,
                        }),
                        onKeyDown: (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onCompanyStatusClick({
                              companyName: company.companyName,
                              status: statusItem.status,
                              count: statusItem.count,
                            });
                          }
                        },
                      }
                      : {
                        className: 'leadership-company-stack-segment',
                      };

                    return (
                      <span
                        key={`${company.companyName}-${statusItem.status}`}
                        {...segmentProps}
                        style={{
                          width: `${widthPercent}%`,
                          backgroundColor: getLeadershipChartBarColor(statusItem.status),
                        }}
                        title={`${statusItem.status}: ${statusItem.count}`}
                      />
                    );
                  })}
                </div>
              </div>
              {onCompanyTotalClick ? (
                <button
                  type="button"
                  className="leadership-count-badge"
                  onClick={() => onCompanyTotalClick({
                    companyName: company.companyName,
                    total: company.total,
                  })}
                  title={`View all ${company.total} projects in PAE Queue`}
                  aria-label={`View all ${company.total} projects for ${rowLabel}`}
                >
                  {company.total}
                </button>
              ) : (
                <strong className="leadership-company-stack-total">{company.total}</strong>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeadershipCompanyStackedChart;
